import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BillingPortalButton } from './BillingPortalButton';
import { formatDistanceToNow } from 'date-fns';

interface CurrentPlanCardProps {
  subscription: any;
  shopId?: string;
}

export const CurrentPlanCard = ({ subscription, shopId }: CurrentPlanCardProps) => {
  if (!subscription) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-2">No Active Subscription</h2>
        <p className="text-muted-foreground">
          Choose a plan below to get started with OptiBay.
        </p>
      </Card>
    );
  }

  const getStatusBadge = () => {
    switch (subscription.status) {
      case 'scheduled':
        return <Badge variant="secondary">Beta Billing Scheduled</Badge>;
      case 'active':
        return <Badge variant="default">Active</Badge>;
      case 'past_due':
        return <Badge variant="destructive">Past Due</Badge>;
      default:
        return <Badge variant="outline">{subscription.status}</Badge>;
    }
  };

  const getPlanName = () => {
    switch (subscription.plan_key) {
      case 'core':
        return 'OptiBay Core';
      case 'catalyst':
        return 'OptiBay Catalyst';
      case 'enterprise':
        return 'OptiBay Enterprise';
      default:
        return subscription.plan_key;
    }
  };

  return (
    <Card className="p-6" data-testid="current-plan-card">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-2xl font-semibold">{getPlanName()}</h2>
          <p className="text-muted-foreground">Your current subscription</p>
        </div>
        {getStatusBadge()}
      </div>

      <div className="space-y-2 mb-6">
        {subscription.status === 'scheduled' && (
          <div>
            <p className="text-sm text-muted-foreground">
              60 days free, then $174.50/mo for 12 months, then $349/mo.
            </p>
          </div>
        )}

        {subscription.status === 'active' && (
          <div>
            <span className="text-sm text-muted-foreground">Next billing date: </span>
            <span className="font-medium">
              {new Date(subscription.current_period_end).toLocaleDateString()}
            </span>
          </div>
        )}

        {subscription.status === 'past_due' && subscription.grace_period_end && (
          <div className="text-destructive">
            <span className="text-sm">Payment required within </span>
            <span className="font-medium">
              {formatDistanceToNow(new Date(subscription.grace_period_end))}
            </span>
          </div>
        )}
      </div>

      <BillingPortalButton shopId={shopId} />
    </Card>
  );
};
